import { PDFDocument, PDFFont, PDFPage, StandardFonts, rgb } from 'pdf-lib'

export interface PdfChildRow {
  nom: string
  prenom: string
  priceType: 'local' | 'local_reduced'
  schoolName: string | null
  parentCpostal: string | null
}

export interface PdfWeekSection {
  semaine: string
  startDate: string
  endDate: string
  children: PdfChildRow[]
}

export interface PdfParams {
  centerName: string
  periode: string
  year: number | string
  sections: PdfWeekSection[]
}

function cleanText(s: string | null | undefined, max = 60): string {
  if (!s) return ''
  const norm = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\x20-\x7E]/g, '?')
  return norm.length > max ? norm.slice(0, max - 1) + '.' : norm
}

function formatFrDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

const PAGE_W = 842
const PAGE_H = 595
const MARGIN_L = 40
const MARGIN_R = 40
const MARGIN_TOP = 45
const MARGIN_BOT = 40
const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R

const COLS: Array<{ key: string; label: string; width: number }> = [
  { key: 'idx',    label: 'N°',        width: 26 },
  { key: 'nom',    label: 'Nom',       width: 160 },
  { key: 'prenom', label: 'Prenom',    width: 130 },
  { key: 'status', label: 'Statut',    width: 90 },
  { key: 'school', label: 'Ecole',     width: CONTENT_W - 26 - 160 - 130 - 90 - 90 },
  { key: 'cp',     label: 'CP parent', width: 90 },
]

const ROW_H = 16
const HEADER_ROW_H = 18

export async function generateCommuneVerificationPdf(params: PdfParams): Promise<Uint8Array> {
  const pdf = await PDFDocument.create()
  const font = await pdf.embedFont(StandardFonts.Helvetica)
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold)

  let page: PDFPage = pdf.addPage([PAGE_W, PAGE_H])
  let y = PAGE_H - MARGIN_TOP

  const ensure = (need: number) => {
    if (y - need < MARGIN_BOT) {
      page = pdf.addPage([PAGE_W, PAGE_H])
      y = PAGE_H - MARGIN_TOP
      drawPageHeader()
    }
  }

  const drawPageHeader = () => {
    page.drawText('VERIFICATION FACTURATION COMMUNE', {
      x: MARGIN_L, y, size: 13, font: bold, color: rgb(0.15, 0.35, 0.6),
    })
    y -= 16
    page.drawText(`Centre : ${cleanText(params.centerName, 40)}`, { x: MARGIN_L, y, size: 9, font, color: rgb(0.2, 0.2, 0.2) })
    page.drawText(`Periode : ${cleanText(params.periode, 20)} ${params.year}`, { x: MARGIN_L + 260, y, size: 9, font, color: rgb(0.2, 0.2, 0.2) })
    page.drawText(`Genere le ${new Date().toLocaleDateString('fr-FR')}`, { x: MARGIN_L + 500, y, size: 9, font, color: rgb(0.5, 0.5, 0.5) })
    y -= 14
  }

  const drawTableHeader = () => {
    ensure(HEADER_ROW_H + 4)
    let x = MARGIN_L
    for (const c of COLS) {
      page.drawRectangle({
        x, y: y - HEADER_ROW_H, width: c.width, height: HEADER_ROW_H,
        color: rgb(0.9, 0.94, 0.98), borderColor: rgb(0.55, 0.6, 0.7), borderWidth: 0.5,
      })
      page.drawText(c.label, { x: x + 4, y: y - HEADER_ROW_H + 6, size: 8.5, font: bold, color: rgb(0.15, 0.25, 0.4) })
      x += c.width
    }
    y -= HEADER_ROW_H
  }

  const drawRow = (row: PdfChildRow, idx: number, zebra: boolean) => {
    ensure(ROW_H + 2)
    let x = MARGIN_L
    for (const c of COLS) {
      page.drawRectangle({
        x, y: y - ROW_H, width: c.width, height: ROW_H,
        borderColor: rgb(0.85, 0.85, 0.85), borderWidth: 0.3,
        color: zebra ? rgb(0.98, 0.98, 0.98) : rgb(1, 1, 1),
      })
      x += c.width
    }
    x = MARGIN_L
    const values: string[] = [
      String(idx),
      cleanText(row.nom.toUpperCase(), 30),
      cleanText(row.prenom, 24),
      row.priceType === 'local_reduced' ? 'Local reduit' : 'Local',
      row.schoolName ? cleanText(row.schoolName, 46) : '(autre)',
      row.parentCpostal ? cleanText(row.parentCpostal, 12) : '',
    ]
    for (let i = 0; i < COLS.length; i++) {
      page.drawText(values[i], { x: x + 4, y: y - ROW_H + 5, size: 8.5, font, color: rgb(0.1, 0.1, 0.1) })
      x += COLS[i].width
    }
    y -= ROW_H
  }

  const drawSectionTitle = (section: PdfWeekSection) => {
    ensure(24)
    page.drawText(
      `Semaine ${cleanText(section.semaine, 20)}  (${formatFrDate(section.startDate)} - ${formatFrDate(section.endDate)})`,
      { x: MARGIN_L, y: y - 12, size: 11, font: bold, color: rgb(0.1, 0.3, 0.5) },
    )
    y -= 18
  }

  const drawSubtotal = (section: PdfWeekSection) => {
    const local = section.children.filter(c => c.priceType === 'local').length
    const localRed = section.children.filter(c => c.priceType === 'local_reduced').length
    ensure(20)
    page.drawText(
      `Sous-total semaine : ${local} local + ${localRed} local reduit = ${local + localRed} enfants`,
      { x: MARGIN_L, y: y - 12, size: 9.5, font: bold, color: rgb(0.15, 0.4, 0.2) },
    )
    y -= 18
  }

  drawPageHeader()
  y -= 6

  const grandTotals = { local: 0, localReduced: 0 }
  for (const section of params.sections) {
    drawSectionTitle(section)
    if (section.children.length === 0) {
      ensure(16)
      page.drawText('Aucun enfant local retenu pour cette semaine.', {
        x: MARGIN_L, y: y - 10, size: 9, font, color: rgb(0.55, 0.55, 0.55),
      })
      y -= 18
      continue
    }
    drawTableHeader()
    section.children.forEach((c, i) => drawRow(c, i + 1, i % 2 === 1))
    drawSubtotal(section)
    y -= 6
    grandTotals.local += section.children.filter(c => c.priceType === 'local').length
    grandTotals.localReduced += section.children.filter(c => c.priceType === 'local_reduced').length
  }

  ensure(24)
  page.drawLine({ start: { x: MARGIN_L, y }, end: { x: PAGE_W - MARGIN_R, y }, thickness: 0.8, color: rgb(0.4, 0.4, 0.4) })
  y -= 14
  page.drawText(
    `TOTAL PERIODE : ${grandTotals.local} local + ${grandTotals.localReduced} local reduit = ${grandTotals.local + grandTotals.localReduced} enfants (avec doublons entre semaines possibles)`,
    { x: MARGIN_L, y, size: 10, font: bold, color: rgb(0.15, 0.35, 0.6) },
  )

  const pages = pdf.getPages()
  const totalPages = pages.length
  pages.forEach((p, i) => {
    p.drawText(`Page ${i + 1} / ${totalPages}`, {
      x: PAGE_W - MARGIN_R - 60, y: 20, size: 8, font, color: rgb(0.5, 0.5, 0.5),
    })
  })

  return pdf.save()
}
